import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  AGENT_LLM_DEFAULT,
  AGENT_LLM_PRESETS,
  buildAgentLlmPresetValue,
  getAgentLlmDisplayLabel,
  parseAgentLlmPresetValue,
  resolveAgentLlmPresetValue,
} from './agentLlmOptions';
import { useQueryClient } from '@tanstack/react-query';
import { updateAgent } from 'features/agents/api';

const DEFAULT_OPTION = {
  value: 'default',
  label: AGENT_LLM_DEFAULT.label,
  description: 'Server default when no model is saved for this agent',
};

const agentName = (agent) =>
  String(agent?.display_name || agent?.name || '').trim() || 'Agent';

/**
 * Reusable model picker. Agent chat passes an agent row. Initiative Agents
 * pass the Initiative's own llm_provider/llm_model and an onSave that writes
 * those columns on the Initiative.
 */
const AgentLlmQuickSelect = ({
  agent,
  className = '',
  displayName = null,
  isLoading = false,
  isSaving: externalSaving = false,
  onSave = null,
  scopeDescription = null,
}) => {
  const queryClient = useQueryClient();
  const [agentSaving, setAgentSaving] = useState(false);
  const [preset, setPreset] = useState('default');
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const resolvedDisplayName = String(displayName || '').trim() || agentName(agent);

  useEffect(() => {
    setPreset(resolveAgentLlmPresetValue(agent));
  }, [agent]);

  const isSaving = externalSaving || agentSaving;

  const menuOptions = useMemo(
    () => [
      DEFAULT_OPTION,
      ...AGENT_LLM_PRESETS.map((option) => ({
        value: buildAgentLlmPresetValue(option.provider, option.model),
        label: option.label,
        description: `${option.provider} · ${option.model}`,
      })),
    ],
    []
  );

  const displayLabel = getAgentLlmDisplayLabel(agent);
  const isNonDefault = preset !== 'default';

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 280;
    const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8);
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left: Math.max(8, left),
      width: menuWidth,
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      const inTrigger = triggerRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) setIsOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelect = async (nextPreset) => {
    if (nextPreset === preset || isSaving || (!agent && !onSave)) return;
    setPreset(nextPreset);
    setIsOpen(false);

    const { provider: llm_provider, model: llm_model } =
      parseAgentLlmPresetValue(nextPreset);

    try {
      if (onSave) {
        await onSave({ llm_provider, llm_model });
      } else {
        setAgentSaving(true);
        try {
          await updateAgent(agent.id, { llm_provider, llm_model });
          await queryClient.invalidateQueries({ queryKey: ['agents'] });
        } finally {
          setAgentSaving(false);
        }
      }
      toast.success(`${resolvedDisplayName} AI model updated`);
    } catch (error) {
      setPreset(resolveAgentLlmPresetValue(agent));
      toast.error(error?.message || `Failed to update ${resolvedDisplayName} AI model`);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`AI model for ${resolvedDisplayName}: ${displayLabel}`}
        disabled={isLoading || isSaving || (!agent && !onSave)}
        onClick={() => setIsOpen((previous) => !previous)}
        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
          isOpen || isNonDefault
            ? 'bg-sky-100 text-sky-600'
            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
        } ${className}`}
        title={`AI model for ${resolvedDisplayName}: ${displayLabel}`}
      >
        {isLoading || isSaving ? (
          <Loader2 size={15} className="animate-spin" aria-hidden />
        ) : (
          <Sparkles size={15} aria-hidden />
        )}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            role="listbox"
            aria-label={`AI model for ${resolvedDisplayName}`}
            className="z-[9999] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5"
          >
            <div className="border-b border-gray-100 px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                AI model
              </div>
              <div className="mt-0.5 truncate text-xs font-medium text-gray-800">
                {resolvedDisplayName}
              </div>
              <div className="truncate text-xs text-gray-600">{displayLabel}</div>
              {scopeDescription ? (
                <div className="mt-1 text-[11px] leading-4 text-gray-500">
                  {scopeDescription}
                </div>
              ) : null}
            </div>
            <div className="max-h-72 overflow-y-auto p-1.5">
              {menuOptions.map((option) => {
                const isSelected = preset === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option.value)}
                    disabled={isSaving}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition disabled:opacity-60 ${
                      isSelected
                        ? 'bg-sky-50 text-sky-900 ring-1 ring-sky-200'
                        : 'text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <Sparkles
                      className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${
                        isSelected ? 'text-sky-600' : 'text-sky-400'
                      }`}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium leading-tight">
                        {option.label}
                      </span>
                      {option.description ? (
                        <span className="mt-0.5 block truncate text-[11px] leading-snug text-gray-500">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    {isSelected ? (
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0 text-sky-600"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AgentLlmQuickSelect;
